import type {CSSProperties} from 'react';
import {WEDGE_PAYWALL} from '@territory/tokens';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgePaywallLook = 'paywall-toggle' | 'paywall-toggle-on' | 'paywall-restore' | 'paywall-legal' | 'paywall-confirm' | 'paywall-cancel';

const PAYWALL_LOOKS = new Set<string>(['paywall-toggle', 'paywall-toggle-on', 'paywall-restore', 'paywall-legal', 'paywall-confirm', 'paywall-cancel']);
export const isWedgePaywallLook = (look: string | undefined): look is WedgePaywallLook => look !== undefined && PAYWALL_LOOKS.has(look);

/* Released Paywall and subscribe confirm buttons. The toggle is one option of the Monthly and Annual pair: 12.5px type, 8px by 14px of
   padding and a 9px radius; the chosen option (`-on`) takes the accent fill, the fixed white ink and the fixed soft shadow. Restore
   Purchases is the bare accent text of the iOS shell, which waits and dims to 60% while it runs (`disabled`). The legal links
   (Terms of Use, Privacy Policy) are bare underlined 12px tertiary text. The confirm is the full width, 50px tall action that fills
   with the tier accent the caller sets in `--wedge-paywall-accent`, under the on-accent ink; while it runs (`disabled`) it waits and
   dims to 60%. Cancel is the bare block under it: 15px secondary ink, 14px of padding, which dims to 40% with the default cursor
   while the purchase runs. Each keeps the declaration order of its source and reads the exact legacy roles. Cancel declares no
   font family, as the source. None draws a transparent target or an anchor: legacy geometry holds and the controls sit apart. */
export function wedgePaywallStyle(look: WedgePaywallLook, disabled: boolean): CSSProperties {
  const r = wedgeExactRole;
  switch (look) {
    case 'paywall-toggle':
    case 'paywall-toggle-on': {
      const on = look === 'paywall-toggle-on';
      return {
        padding: '8px 14px', borderRadius: 9, border: 0,
        background: on ? r('accent') : 'transparent',
        color: on ? WEDGE_PAYWALL.toggleInk : r('labelSec'),
        fontSize: 12.5, fontWeight: on ? 700 : 600,
        letterSpacing: -0.1, cursor: 'pointer', fontFamily: r('font'),
        transition: `all 160ms ${r('ease')}`,
        boxShadow: on ? WEDGE_PAYWALL.toggleShadow : 'none',
      };
    }
    case 'paywall-restore': return {
      background: 'none', border: 0, cursor: disabled ? 'wait' : 'pointer',
      fontFamily: r('font'), fontSize: 13, fontWeight: 600,
      color: r('accent'), padding: '8px 14px', opacity: disabled ? 0.6 : 1,
    };
    case 'paywall-legal': return {
      background: 'none', border: 0, cursor: 'pointer', fontFamily: r('font'),
      fontSize: 12, fontWeight: 600, color: r('labelTer'), padding: '4px 6px', textDecoration: 'underline',
    };
    case 'paywall-confirm': return {
      width: '100%', height: 50, borderRadius: 14,
      background: 'var(--wedge-paywall-accent)', color: r('onAccent'), border: 0,
      cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.6 : 1,
      fontSize: 16, fontWeight: 600, fontFamily: r('font'),
    };
    case 'paywall-cancel': return {
      display: 'block', width: '100%',
      background: 'none', border: 0,
      color: r('labelSec'), fontSize: 15, fontWeight: 500,
      padding: 14, marginTop: 4, cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.4 : 1,
    };
  }
}
